import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  ToolRefused,
  runAssistantTool,
  todayIn,
  toolsFor,
  loadJobCalendar,
  systemClock,
  type AssistantTool,
  type ShiftProposal,
} from '@buildmate/core';
import { Button } from '@buildmate/ui-web';
import { usePorts } from '../lib/ports-context.tsx';
import { useSession } from '../lib/session-context.tsx';
import { AssistantProposal } from './AssistantProposal.tsx';

/**
 * The assistant, in a side pane (SCRUM-223, Phase A).
 *
 * **The agent loop lives here, in the browser.** The relay function holds
 * the key and calls the model; when the model asks for a tool, this runs
 * it through the ordinary ports — the person's own session — and sends
 * the result back for the next turn. So Firestore refuses the assistant
 * on exactly the documents it refuses them, and there is no second set of
 * permission rules to keep in step with the first.
 *
 * **It reads and does not write.** A change is a *proposal*: the tool
 * returns what would happen, this pane puts it in front of a person, and
 * `AssistantProposal` is the only thing that can turn one into a write
 * (Phase B). The model has no way to press that button, which is the
 * structural half of the defence against a sentence in somebody's daily
 * log moving a date.
 *
 * **One proposal at a time.** A second one replaces the first rather than
 * stacking, because two amber cards offering to move the same job is two
 * buttons where only one can still be right.
 */
const MAX_STEPS = 12;

interface Turn {
  readonly who: 'you' | 'assistant' | 'tool';
  readonly text: string;
}

/** The provider's message shape, which this component only passes through. */
type Message = { role: 'user' | 'assistant'; content: unknown };

export function AssistantPane({ onClose }: { readonly onClose: () => void }) {
  const ports = usePorts();
  const { membership, company, principal, user } = useSession();
  const [turns, setTurns] = useState<readonly Turn[]>([]);
  const [messages, setMessages] = useState<readonly Message[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [proposal, setProposal] = useState<ShiftProposal | null>(null);
  const log = useRef<HTMLDivElement | null>(null);

  const tools: AssistantTool[] = toolsFor(principal ?? null);
  const today = todayIn(company?.timeZone ?? 'UTC', new Date());

  useEffect(() => {
    log.current?.scrollTo({ top: log.current.scrollHeight });
  }, [turns, proposal]);

  async function ask(event: FormEvent) {
    event.preventDefault();
    const question = draft.trim();
    if (!question || busy) return;

    setDraft('');
    setError(null);
    setBusy(true);
    setTurns((previous) => [...previous, { who: 'you', text: question }]);
    // A new question retires the old offer. Whatever it was computed
    // against, the conversation has moved on.
    setProposal(null);

    let conversation: Message[] = [...messages, { role: 'user', content: question }];

    try {
      for (let step = 0; step < MAX_STEPS; step += 1) {
        const reply = await ports.functions.call('askAssistant', {
          companyId: membership.companyId,
          messages: conversation,
          model: company?.assistantModel ?? 'claude-sonnet-5',
          step,
        });

        setRemaining(reply.remaining);
        if (reply.text) {
          setTurns((previous) => [...previous, { who: 'assistant', text: reply.text }]);
        }

        if (reply.toolUses.length === 0) {
          conversation = [...conversation, { role: 'assistant', content: reply.text }];
          break;
        }

        // The model asked for data. Run each tool **here**, in this
        // session, and hand back what it returns — as a result, never as
        // something that can instruct the next turn.
        conversation = [
          ...conversation,
          {
            role: 'assistant',
            content: [
              ...(reply.text ? [{ type: 'text', text: reply.text }] : []),
              ...reply.toolUses.map((use) => ({
                type: 'tool_use',
                id: use.id,
                name: use.name,
                input: use.input,
              })),
            ],
          },
        ];

        const results = [];
        for (const use of reply.toolUses) {
          setTurns((previous) => [...previous, { who: 'tool', text: labelFor(tools, use.name) }]);
          try {
            const value = await runAssistantTool(
              {
                data: ports.data,
                companyId: membership.companyId,
                today,
                /**
                 * The job's own working calendar, loaded through the same
                 * use case every schedule screen uses.
                 *
                 * A proposal's dates have to be counted over this job's
                 * work week and holidays exactly as the write counts
                 * them, or the preview is wrong across every weekend —
                 * and the preview is what somebody agrees to.
                 */
                loadCalendar: (jobId) =>
                  loadJobCalendar({
                    data: ports.data,
                    clock: systemClock,
                    companyId: membership.companyId,
                    jobId,
                    uid: user.uid,
                  }),
              },
              use.name,
              use.input,
            );
            /**
             * A proposal is **held here as well as handed back**.
             *
             * The model gets it so it can explain what it is offering; the
             * pane keeps it so a person can agree to it. The two are not
             * the same thing and only one of them can write: the card
             * reads from this state, never from anything the model says
             * afterwards, so a model that invented a different set of
             * dates in its reply could not change what the button does.
             */
            const offered = proposalIn(value);
            if (offered) setProposal(offered);
            results.push({
              type: 'tool_result',
              tool_use_id: use.id,
              content: JSON.stringify(value),
            });
          } catch (cause) {
            // A refusal the model can report, not an empty result it
            // would relay as "there is nothing". A permission error in
            // particular should read as "your role does not include
            // that", which the system prompt asks it to say.
            results.push({
              type: 'tool_result',
              tool_use_id: use.id,
              is_error: true,
              content:
                cause instanceof ToolRefused
                  ? cause.message
                  : cause instanceof Error
                    ? `That could not be read: ${cause.message}`
                    : 'That could not be read.',
            });
          }
        }
        conversation = [...conversation, { role: 'user', content: results }];
      }

      setMessages(conversation);
    } catch (cause) {
      console.error('[buildmate] the assistant failed', cause);
      const message = cause instanceof Error ? cause.message : '';
      setError(
        /allowance|exhausted/i.test(message)
          ? 'This company has used its assistant allowance for the month.'
          : /not switched on/i.test(message)
            ? 'The assistant is not switched on for this company yet.'
            : 'The assistant could not answer. Nothing was changed.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <aside
      aria-label="Assistant"
      className="flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white"
    >
      <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Assistant</h2>
          <p className="text-xs text-slate-500">
            It reads what you can read. Any change it suggests is yours to confirm.
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </header>

      <div ref={log} className="flex-1 overflow-y-auto px-4 py-3">
        {turns.length === 0 && (
          <div className="text-sm text-slate-600">
            <p className="font-medium text-slate-900">Ask about your jobs, or about Buildmate.</p>
            <ul className="mt-2 flex flex-col gap-1.5">
              {[
                'What equipment is being used next Monday?',
                'Which jobs are open?',
                'How do change orders work here?',
              ].map((example) => (
                <li key={example}>
                  <button
                    type="button"
                    onClick={() => setDraft(example)}
                    className="text-left text-blue-700 underline-offset-2 hover:underline"
                  >
                    {example}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <ul className="flex flex-col gap-3">
          {turns.map((turn, index) => (
            <li
              key={`${turn.who}-${index}`}
              className={
                turn.who === 'you'
                  ? 'self-end rounded-lg bg-blue-50 px-3 py-2 text-sm text-slate-900'
                  : turn.who === 'tool'
                    ? 'text-xs text-slate-500'
                    : 'whitespace-pre-wrap text-sm text-slate-800'
              }
            >
              {turn.who === 'tool' ? `· ${turn.text}` : turn.text}
            </li>
          ))}
        </ul>

        {proposal && (
          <div className="mt-3">
            <AssistantProposal
              proposal={proposal}
              onDone={(outcome) => {
                setProposal(null);
                setTurns((previous) => [...previous, { who: 'assistant', text: outcome }]);
              }}
              onDismiss={() => {
                setProposal(null);
                setTurns((previous) => [
                  ...previous,
                  { who: 'tool', text: 'left the schedule as it was' },
                ]);
              }}
            />
          </div>
        )}

        {busy && <p className="mt-3 text-xs text-slate-500">Thinking…</p>}
        {error && (
          <p role="alert" className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}
      </div>

      <form onSubmit={ask} className="border-t border-slate-200 p-3">
        <label className="sr-only" htmlFor="assistant-question">
          Ask the assistant
        </label>
        <textarea
          id="assistant-question"
          rows={2}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            // Enter sends, Shift+Enter is a new line — the convention
            // everywhere else people type into a chat.
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void ask(event as unknown as FormEvent);
            }
          }}
          placeholder="Ask about your jobs, your schedule or your plant"
          className="w-full resize-none rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline focus:outline-2 focus:outline-blue-500"
        />
        <div className="mt-2 flex items-center justify-between">
          {/* Shown rather than discovered when it stops: the allowance is
              what the company is billed against. */}
          <span className="text-xs text-slate-500">
            {remaining === null ? '' : `${remaining.toLocaleString()} tokens left this month`}
          </span>
          <Button type="submit" size="sm" loading={busy} disabled={!draft.trim()}>
            Ask
          </Button>
        </div>
      </form>
    </aside>
  );
}

/** What a tool call looks like in the log: a sentence, not a function name. */
function labelFor(tools: readonly AssistantTool[], key: string): string {
  const tool = tools.find((candidate) => candidate.key === key);
  return tool ? tool.label.toLowerCase() : `running ${key}`;
}

/**
 * The proposal in a tool result, if there is one.
 *
 * Narrowed by shape rather than trusted: this value came back from
 * `runAssistantTool`, so it is this app's own, but the card it feeds can
 * write to the schedule and a `kind` check is cheap. `itemIds` and
 * `byWorkingDays` are what the write is actually built from, so those are
 * the two that have to be right.
 */
function proposalIn(value: unknown): ShiftProposal | null {
  if (typeof value !== 'object' || value === null) return null;
  const offered = (value as { proposal?: unknown }).proposal;
  if (typeof offered !== 'object' || offered === null) return null;
  const candidate = offered as Partial<ShiftProposal>;
  if (candidate.kind !== 'shiftWork') return null;
  if (!Array.isArray(candidate.itemIds) || typeof candidate.byWorkingDays !== 'number') return null;
  if (!Array.isArray(candidate.changes) || typeof candidate.jobId !== 'string') return null;
  return offered as ShiftProposal;
}
